import { useState, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { useApi, ApiError } from '../lib/api.js';
import { PageHeader } from '../components/PageHeader.js';
import { toMinor, minorDivisor, type Currency } from '@chatrunner/schemas';

interface VariantDraft {
  variant_name: string;
  size: string;
  color: string;
  price_cents: string;
  image_url: string | null;
  uploading_image: boolean;
  stock_count: string;
  low_stock_threshold: string;
}

const emptyVariant = (): VariantDraft => ({
  variant_name: '',
  size: '',
  color: '',
  price_cents: '',
  image_url: null,
  uploading_image: false,
  stock_count: '0',
  low_stock_threshold: '0',
});

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string) ?? '/api/v1';

export function AddProduct() {
  const api = useApi();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tagsRaw, setTagsRaw] = useState('');
  const [category, setCategory] = useState('');
  const [priceCents, setPriceCents] = useState('');
  const [originalPriceCents, setOriginalPriceCents] = useState('');
  const [currency, setCurrency] = useState<Currency>('IQD');
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [uploadingProduct, setUploadingProduct] = useState(false);
  const [variants, setVariants] = useState<VariantDraft[]>([]);
  const [error, setError] = useState<string | null>(null);
  const productFileRef = useRef<HTMLInputElement>(null);

  async function uploadFile(file: File): Promise<string> {
    if (!file.type.startsWith('image/')) throw new Error('Only image files are allowed');
    if (file.size > 10 * 1024 * 1024) throw new Error('Image must be under 10 MB');
    const token = await getToken();
    const res = await fetch(`${API_BASE}/uploads/product-image`, {
      method: 'PUT',
      headers: {
        'content-type': file.type,
        authorization: token ? `Bearer ${token}` : '',
      },
      body: file,
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`Upload failed: ${res.status} ${body.slice(0, 200)}`);
    }
    const data = (await res.json()) as { url: string };
    return data.url;
  }

  async function handleProductFiles(files: FileList) {
    setUploadingProduct(true);
    setError(null);
    try {
      const slots = Math.min(files.length, 8 - imageUrls.length);
      const uploaded: string[] = [];
      for (let i = 0; i < slots; i++) {
        uploaded.push(await uploadFile(files[i]!));
      }
      setImageUrls((arr) => [...arr, ...uploaded]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUploadingProduct(false);
      if (productFileRef.current) productFileRef.current.value = '';
    }
  }

  async function handleVariantFile(idx: number, file: File) {
    setVariants((arr) => arr.map((v, j) => (j === idx ? { ...v, uploading_image: true } : v)));
    try {
      const url = await uploadFile(file);
      setVariants((arr) => arr.map((v, j) => (j === idx ? { ...v, image_url: url, uploading_image: false } : v)));
    } catch (e) {
      setError((e as Error).message);
      setVariants((arr) => arr.map((v, j) => (j === idx ? { ...v, uploading_image: false } : v)));
    }
  }

  const create = useMutation({
    mutationFn: async () => {
      if (!title.trim()) throw new Error('Title is required');
      if (imageUrls.length === 0) throw new Error('Upload at least one image');
      const priceNum = Number(priceCents);
      if (!Number.isFinite(priceNum) || priceNum < 0) throw new Error('Price must be a non-negative number');

      const payload = {
        title: title.trim(),
        description: description.trim(),
        tags: tagsRaw.split(',').map((t) => t.trim()).filter(Boolean),
        category: category.trim() || null,
        // Form takes major units (12000 = 12,000 IQD); persist in minor units.
        price_cents: toMinor(priceNum, currency),
        original_price_cents: originalPriceCents ? toMinor(Number(originalPriceCents), currency) : null,
        currency,
        images: imageUrls,
        variants: variants.map((v) => {
          const attrs: Record<string, string> = {};
          if (v.size) attrs['size'] = v.size;
          if (v.color) attrs['color'] = v.color;
          return {
            variant_name:
              v.variant_name.trim() ||
              [v.size, v.color].filter(Boolean).join(' / ') ||
              'Default',
            variant_attributes: attrs,
            price_cents: v.price_cents ? toMinor(Number(v.price_cents), currency) : null,
            image_url: v.image_url ?? null,
            stock_count: Math.max(0, Math.round(Number(v.stock_count) || 0)),
            low_stock_threshold: Math.max(0, Math.round(Number(v.low_stock_threshold) || 0)),
          };
        }),
      };
      return api.post<{ product_id: string }>('/products', payload);
    },
    onSuccess: () => navigate('/products'),
    onError: (e) => setError(e instanceof ApiError ? `${e.code}: ${e.message}` : (e as Error).message),
  });

  return (
    <div className="p-6 max-w-3xl">
      <PageHeader title="Add product" subtitle="Files are uploaded to R2. After save, the product is vectorized (text + primary image) and becomes searchable within a minute." />

      {error && <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 mb-4">{error}</div>}

      <section className="space-y-4">
        <Field label="Title">
          <input value={title} onChange={(e) => setTitle(e.target.value)} className={input} placeholder="e.g. Cotton Abaya" />
        </Field>

        <Field label="Description">
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} className={input} placeholder="Soft cotton, breathable. Available in summer-friendly cuts." />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Tags (comma-separated)">
            <input value={tagsRaw} onChange={(e) => setTagsRaw(e.target.value)} className={input} placeholder="abaya, cotton, summer" />
          </Field>
          <Field label="Category">
            <input value={category} onChange={(e) => setCategory(e.target.value)} className={input} placeholder="Apparel" />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label={`Sale price (${currency}${minorDivisor(currency) === 1 ? '' : ' major units, e.g. 45.00'})`}>
            <input value={priceCents} onChange={(e) => setPriceCents(e.target.value)} className={input} inputMode="decimal" placeholder={minorDivisor(currency) === 1 ? '12000' : '45.00'} />
          </Field>
          <Field label="Original price (optional, strikethrough)">
            <input value={originalPriceCents} onChange={(e) => setOriginalPriceCents(e.target.value)} className={input} inputMode="decimal" placeholder={minorDivisor(currency) === 1 ? '22000' : '60.00'} />
          </Field>
          <Field label="Currency">
            <select value={currency} onChange={(e) => setCurrency(e.target.value as Currency)} className={input}>
              <option value="IQD">IQD</option>
              <option value="USD">USD</option>
              <option value="SAR">SAR</option>
              <option value="AED">AED</option>
              <option value="EGP">EGP</option>
              <option value="JOD">JOD</option>
            </select>
          </Field>
        </div>

        <Field label="Product images (first one is the primary — used for AI search)">
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {imageUrls.map((url, i) => (
                <div key={url} className="relative">
                  <img src={url} alt="" className="w-24 h-24 object-cover rounded border border-slate-200" />
                  <button
                    type="button"
                    onClick={() => setImageUrls((arr) => arr.filter((_, j) => j !== i))}
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-600 text-white text-xs"
                    aria-label="Remove image"
                  >×</button>
                  {i === 0 && (
                    <span className="absolute bottom-1 left-1 text-[10px] bg-brand-600 text-white px-1 rounded">primary</span>
                  )}
                </div>
              ))}
              {imageUrls.length < 8 && (
                <label className={`w-24 h-24 rounded border-2 border-dashed flex items-center justify-center text-xs cursor-pointer ${uploadingProduct ? 'border-slate-200 text-slate-400' : 'border-slate-300 text-slate-500 hover:border-brand-500 hover:text-brand-600'}`}>
                  {uploadingProduct ? 'Uploading…' : '+ upload'}
                  <input
                    ref={productFileRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    disabled={uploadingProduct}
                    onChange={(e) => e.target.files && handleProductFiles(e.target.files)}
                  />
                </label>
              )}
            </div>
            <p className="text-xs text-slate-500">JPG / PNG / WebP / GIF. Max 10 MB each, up to 8 images.</p>
          </div>
        </Field>

        <section className="border-t border-slate-200 pt-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium">Variants</h3>
            <button type="button" onClick={() => setVariants([...variants, emptyVariant()])} className="text-xs rounded-md border border-slate-300 px-2 py-1 hover:bg-slate-50">+ add variant</button>
          </div>
          {variants.length === 0 && (
            <p className="text-sm text-slate-500">No variants. The product will be sold with one default SKU using the price + stock you set above.</p>
          )}
          <div className="space-y-3">
            {variants.map((v, idx) => (
              <div key={idx} className="rounded-lg border border-slate-200 p-3 space-y-2 bg-white">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-slate-500">Variant #{idx + 1}</div>
                  <button type="button" onClick={() => setVariants(variants.filter((_, j) => j !== idx))} className="text-xs text-red-600 hover:underline">remove</button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input placeholder="Size (e.g. M)" value={v.size} onChange={(e) => setVariants(variants.map((x, j) => j === idx ? { ...x, size: e.target.value } : x))} className={input} />
                  <input placeholder="Color (e.g. red)" value={v.color} onChange={(e) => setVariants(variants.map((x, j) => j === idx ? { ...x, color: e.target.value } : x))} className={input} />
                </div>
                <input placeholder="Display name (auto-built from size/color if blank)" value={v.variant_name} onChange={(e) => setVariants(variants.map((x, j) => j === idx ? { ...x, variant_name: e.target.value } : x))} className={input} />
                <div className="grid grid-cols-3 gap-2">
                  <input inputMode="numeric" placeholder="Stock count" value={v.stock_count} onChange={(e) => setVariants(variants.map((x, j) => j === idx ? { ...x, stock_count: e.target.value } : x))} className={input} />
                  <input inputMode="numeric" placeholder="Low-stock alert" value={v.low_stock_threshold} onChange={(e) => setVariants(variants.map((x, j) => j === idx ? { ...x, low_stock_threshold: e.target.value } : x))} className={input} />
                  <input inputMode="numeric" placeholder="Price override (optional)" value={v.price_cents} onChange={(e) => setVariants(variants.map((x, j) => j === idx ? { ...x, price_cents: e.target.value } : x))} className={input} />
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Variant image (optional)</div>
                  <div className="flex gap-2 items-center">
                    {v.image_url && (
                      <div className="relative">
                        <img src={v.image_url} alt="" className="w-16 h-16 object-cover rounded border border-slate-200" />
                        <button
                          type="button"
                          onClick={() => setVariants((arr) => arr.map((x, j) => j === idx ? { ...x, image_url: null } : x))}
                          className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-600 text-white text-xs"
                        >×</button>
                      </div>
                    )}
                    {!v.image_url && (
                      <label className={`px-2 py-1 rounded border border-slate-300 text-xs cursor-pointer ${v.uploading_image ? 'opacity-50' : 'hover:bg-slate-50'}`}>
                        {v.uploading_image ? 'Uploading…' : 'Upload variant image'}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={v.uploading_image}
                          onChange={(e) => e.target.files?.[0] && handleVariantFile(idx, e.target.files[0])}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={() => navigate('/products')} className="rounded-md border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50">Cancel</button>
          <button onClick={() => create.mutate()} disabled={create.isPending || uploadingProduct || imageUrls.length === 0} className="rounded-md bg-brand-600 text-white px-4 py-2 text-sm font-medium hover:bg-brand-700 disabled:opacity-50">
            {create.isPending ? 'Saving…' : 'Save & vectorize'}
          </button>
        </div>
      </section>
    </div>
  );
}

const input = 'rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 w-full';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-sm font-medium text-slate-700 mb-1">{label}</div>
      {children}
    </label>
  );
}
